import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { PublicUser } from '../lib/types';
import { useAuth } from '../store/auth';

/** The answer, as the form holds it: a teacher's id, or no teacher yet. */
export type RosterChoice = string | null;

/**
 * Who a student is being enrolled to — asked of the administrator only.
 *
 * A teacher's students are their own, always, so a teacher is not asked: the
 * server enrols to them whatever is sent. It used to offer "the whole centre"
 * and default to it, which is how a teacher came to see, edit and reset a
 * colleague's class without anybody having chosen that.
 *
 * The administrator names the teacher, or nobody yet — a student with no
 * teacher is the administrator's own, and is given out later from the
 * register.
 */
export function RosterField({
  value,
  onChange,
  organizationId,
  label = 'Enrol to',
}: {
  value: RosterChoice;
  onChange: (next: RosterChoice) => void;
  /** The centre being worked inside; a platform administrator must name one. */
  organizationId?: string | null;
  label?: string;
}) {
  const me = useAuth((s) => s.user);
  const isAdmin = me?.role === 'SUPER_ADMIN';
  const [teachers, setTeachers] = useState<PublicUser[]>([]);

  useEffect(() => {
    if (!isAdmin || !organizationId) return;
    api
      .get<{ members: PublicUser[] }>('/users', { params: { role: 'INSTRUCTOR', organizationId } })
      .then(({ data }) => setTeachers(data.members))
      .catch(() => setTeachers([]));
  }, [isAdmin, organizationId]);

  if (isAdmin) {
    return (
      <label className="field">
        <span className="field__label">{label}</span>
        <select
          className="field__input"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="">No teacher yet — only you</option>
          {teachers.map((teacher) => (
            <option key={teacher.id} value={teacher.id}>
              {teacher.fullName}
            </option>
          ))}
        </select>
        <span className="field__hint">
          A student enrolled to a teacher appears on that teacher's register and no other
          teacher's. With no teacher yet, only you can see them until you give them to one.
        </span>
      </label>
    );
  }

  // A teacher is not asked: their students are their own.
  return null;
}
